/**
 * Px-based admin rail styles — shared by pages + App Router.
 *
 * Pages use `html { font-size: 62.5% }` (1rem = 10px); App keeps 16px rem for
 * HeroUI/Tailwind. Rem-authored CondensableSidebar cannot look the same in
 * both. These tokens are the pages paint (rem × 10 → px) so one rail works
 * everywhere without zoom / html font-size bridges.
 *
 * Section builder still uses rem `styles/CondensableSidebar.styled.ts`.
 */
import { css, Theme } from '@emotion/react';
import styled from '@emotion/styled';
import NavLink from '@OnArk/NavLink';
import navItemState from '@OnArk/styles/navItemState';

const OPEN_WIDTH_PX = 200;
const CONDENSED_WIDTH_PX = 55;

export const AdminSidebarNavTitle = styled.span(
  ({ theme }) => css`
    display: block;
    margin: 15px 10px 5px;
    overflow: hidden;

    color: ${theme.colors.grayMed};
    font-size: 10px;
    line-height: 10px;
    letter-spacing: 1px;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-transform: uppercase;

    border-bottom: 1px solid transparent;

    transition: all 0.2s;
  `
);

export const AdminSidebarLink = styled(NavLink)<{
  parent?: string;
  selected?: boolean;
}>(
  ({ theme }) => css`
    position: relative;

    display: block;

    padding: 5px 15px;

    color: ${theme.colors.black};
    font-weight: 500;
    font-size: 14px;
    line-height: 1em;
    letter-spacing: 0.5px;

    vertical-align: middle;

    /* Icon.tsx margins are rem — pin to pages (10px) on both routers. */
    i {
      margin-right: 10px !important;
      margin-left: 0 !important;
    }

    ${navItemState(theme)}
  `
);

export const AdminSidebarChurchDropdown = styled.div<{ open: boolean }>(
  ({ open, theme }) => css`
    position: relative;
    z-index: 2;

    width: 100%;

    margin: 0;

    font-size: 12px;

    background: ${theme.colors.white};

    figure {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1;

      display: ${open ? 'block' : 'none'};
      margin: 0;
    }
    small {
      width: auto;
      max-width: 242px;
      margin-left: 16px;
      overflow: hidden;

      font-weight: 500;
      font-size: 12px;
      line-height: 1.3;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    a {
      display: grid;
      grid-template-columns: auto 1fr auto;
      justify-content: flex-start;
      align-items: center;
      width: 100%;
      padding: 0 15px;

      font-size: 12px;

      cursor: pointer;
      small {
        flex: 1;

        text-align: left;
      }
    }
    circle {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 29px;
      height: 29px;

      color: ${theme.colors.grayDark};
      font-weight: 700;
      font-size: 18px;
      line-height: 1.3;

      border: 1px solid ${theme.colors.border};
      border-radius: 50%;

      transition: all 0.2s;

      picture {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 40px;
        overflow: hidden;

        border-radius: 50%;
      }
      span {
        padding: 10px;
      }
      &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
      }
    }

    > nav {
      position: absolute;

      top: 100%;
      right: 0;
      left: 0;

      z-index: 2;

      display: ${open ? 'flex' : 'none'};
      flex-direction: column;
      padding: 10px 0 0;

      overflow: hidden;

      background: ${theme.colors.white};

      border-bottom-right-radius: 3px;
      box-shadow: 4px 4px 4px rgba(0, 0, 0, 0.1);
      a {
        padding: 10px 15px;
      }
    }
  `
);

/**
 * Expand/collapse control — ALWAYS visible (it used to be invisible until
 * the header was hovered, which made the rail feel un-expandable) and sized
 * to a real click target. Pokes 13px past the rail into the content canvas,
 * so its z-index must clear a sticky page header (AdminPageHeader's `sticky`
 * variant renders at z-20) or the header paints over it on any page that
 * scrolls.
 */
export const AdminSidebarToggle = styled.button(
  ({ theme }) => css`
    position: absolute;
    top: 18px;
    right: -13px;
    z-index: 30;

    display: flex;
    justify-content: center;
    align-items: center;
    width: 26px;
    height: 26px;
    padding: 0;

    color: ${theme.colors.white};

    background: ${theme.colors.highlight};
    border: 2px solid ${theme.colors.white};
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(15, 11, 26, 0.25);
    cursor: pointer;

    transition: transform 0.15s;

    &:hover {
      transform: scale(1.12);
    }
  `
);

const condensedCss = (theme: Theme) => css`
  width: ${CONDENSED_WIDTH_PX}px;

  ${AdminSidebarChurchDropdown} {
    a {
      i,
      small {
        width: 0;
        overflow: hidden;
      }
    }
  }
  ${AdminSidebarNavTitle} {
    height: 0;
    /* Keep vertical rhythm close to the open-state title (15+10+5) so group
       positions barely move on collapse — titles become spaced dividers. */
    margin: 14px 10px;
    padding: 0;

    color: ${theme.colors.white};

    border-bottom-color: ${theme.colors.border};
  }
  > aside main > * {
    width: 100% !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    row-gap: 4px !important;
    > * {
      flex: 0 0 auto !important;
    }
  }
  ${AdminSidebarLink} {
    position: relative;

    height: 40px;
    padding: 0;

    font-size: 0;
    > div,
    > span {
      position: absolute;
      top: 50%;
      left: 50%;

      transform: translate(-50%, -50%);
    }
    i {
      margin-right: 0 !important;
      margin-left: 0 !important;
    }
  }
`;

export const AdminSidebarRail = styled.nav<{ condensed: boolean }>(
  ({ theme }) => css`
    /* Stick for the full viewport while the canvas scrolls — blank space
       below the rail was from a 100vh-tall rail scrolling away with the page. */
    position: sticky;
    top: 0;
    align-self: flex-start;
    /* This sticky positioning makes the rail its own stacking context, which
       caps every descendant (including AdminSidebarToggle's own z-index) at
       this value when compared to anything outside the rail — a sticky page
       header (AdminPageHeader's sticky variant, z-20) painted over the
       always-visible toggle at the old z-index: 4, no matter how high the
       toggle's own z-index was set. Must clear the highest sticky z-index any
       page content uses. */
    z-index: 30;

    flex-shrink: 0;
    width: ${OPEN_WIDTH_PX}px;
    max-width: ${OPEN_WIDTH_PX}px;
    height: 100dvh;

    letter-spacing: 0.5px;

    background: ${theme.colors.white};

    transition: width 0.2s;

    > aside {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      grid-template-rows: auto 1fr auto;
      align-items: start;
      width: 100%;
      height: 100%;
      padding-bottom: 15px;

      main {
        height: 100%;
        overflow: hidden;
        > * {
          display: flex;
          flex-direction: column;
          flex-wrap: nowrap;
          justify-content: flex-start;
          width: 100%;
          height: 100%;
          overflow-x: hidden;
          overflow-y: auto;
          scrollbar-width: none;
          &::-webkit-scrollbar {
            display: none;
          }
        }
      }

      a,
      button {
        max-width: 100%;

        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
    header {
      position: relative;

      display: flex;
      align-items: center;
      height: 62px;

      border-bottom: 1px solid ${theme.colors.border};
    }
    footer {
      display: grid;
      border-top: 1px solid ${theme.colors.border};
    }
  `,
  ({ condensed, theme }) => condensed && condensedCss(theme),
  ({ theme }) => css`
    @media (max-width: 991px) {
      ${condensedCss(theme)}
    }
  `
);

export const AdminSidebarSection = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 0;
  width: 100%;
  padding: 0;
`;

/**
 * Nav flows TOP-DOWN — Primary no longer stretches (flex: 1 shoved
 * Support/church/user into one pile at the rail bottom).
 */
export const AdminSidebarPrimary = styled.div`
  width: 100%;
`;

/**
 * Account cluster (user name, Profile, Refer, Log Out) pinned to the rail
 * bottom in BOTH states, so collapsing doesn't slide the icons upward.
 */
export const AdminSidebarBottom = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
  margin-top: auto;
`;

export const AdminSidebarLogoWrap = styled.div`
  display: flex;
  justify-content: center;

  svg {
    width: auto;
    height: 20px;
    margin: 10px auto 0;
  }
`;
